import type { Editor, Extensions } from "@tiptap/core";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import { Placeholder } from "@tiptap/extension-placeholder";
import {
  EditorContent as EditorContentTiptap,
  useEditor as useEditorTiptap,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { cn } from "cn";
import {
  forwardRef,
  type PropsWithChildren,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { isSafari } from "react-device-detect";
import {
  CustomState,
  EditorErrorBoundary,
  EventHandler,
  htmlToMarkdown,
  markdownToHtml,
  ResetContent,
  SelectText,
  SmartEmoji,
  stripTags,
} from "@/components/ui/editor/core";
import { hasAttachmentsDragData } from "@/libs/attachment-drag";
import { ImagePasteUpload } from "../core/extensions/image-paste-upload";
import { useEditorContext } from "./rich-text-editor-provider";

const EMPTY_EXTENSIONS: Extensions = [];

export type EditorContentHandle = {
  setContent: (content: string) => void;
  getContent: () => string;
  focus: () => void;
  blur: () => void;
  isEmpty: () => boolean;
  /** Clears the document AND wipes `editor.storage.customState` (ResetContent extension). */
  resetContent: () => void;
};

type EditorContentProps = PropsWithChildren<{
  extensions?: Extensions;
  placeholder?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: (editor: Editor) => void;
  onUploadImages?: (files: File[]) => void;
  className?: string;
  emptyEditorClass?: string;
  autoFocus?: boolean;
  disabled?: boolean;
}>;

export const EditorContent = forwardRef<
  EditorContentHandle,
  EditorContentProps
>(function EditorContent(
  {
    placeholder,
    defaultValue = "",
    onChange,
    onFocus,
    onBlur,
    onUploadImages,
    className,
    emptyEditorClass,
    autoFocus = false,
    disabled = false,
    extensions: customExtensions = EMPTY_EXTENSIONS,
  },
  ref,
) {
  // Prevent first onChange call to avoid form validation issues
  const isFirstRenderRef = useRef(true);
  const { setEditor } = useEditorContext("Editor");

  // Memoize extensions array to prevent recreating on every render
  const extensions = useMemo(
    () => [
      CustomState,
      SelectText,
      StarterKit.configure({
        link: false,
      }),
      Placeholder.configure({
        placeholder: ({ editor }) => {
          const { doc } = editor.state;
          if (doc.content.childCount < 2) {
            return placeholder || "Typing something...";
          }
          return "";
        },
        showOnlyWhenEditable: false,
        emptyEditorClass: cn(
          /* sorted */
          "cursor-text",
          "before:content-[attr(data-placeholder)] before:absolute before:text-text-dark-min before:pointer-events-none",
          disabled ? "cursor-not-allowed" : "",
          emptyEditorClass,
        ),
      }),
      Image.configure({
        allowBase64: true,
        inline: true,
        HTMLAttributes: {
          "data-type": "emoji",
          class: `inline-block m-0  ${isSafari ? "data-[type=emoji]:size-5" : "data-[type=emoji]:size-4"}`,
        },
      }),
      Link.configure({
        openOnClick: false,
        autolink: false,
      }),
      SmartEmoji,
      EventHandler,
      ResetContent,
      ImagePasteUpload.configure({
        onPasteImages: (files: File[]) => {
          onUploadImages?.(files);
        },
      }),
      ...customExtensions,
    ],
    [placeholder, disabled, emptyEditorClass, customExtensions],
  );

  const editorInstance = useEditorTiptap({
    extensions,
    content: markdownToHtml(defaultValue),
    // Focus at creation time (inside ProseMirror's createView) rather than in a
    // React effect — dispatching a focus transaction from an effect can race the
    // editor's own reconfiguration/teardown and throw "Applying a mismatched transaction".
    autofocus: autoFocus ? "end" : false,
    immediatelyRender: false, // Best practice for SSR and performance
    shouldRerenderOnTransaction: false, // Prevent unnecessary re-renders
    onBeforeCreate() {
      isFirstRenderRef.current = false;
    },
    onCreate() {
      isFirstRenderRef.current = true;
    },
    onUpdate({ editor }) {
      if (!isFirstRenderRef.current) {
        return;
      }
      const html = stripTags(editor.getHTML());
      onChange?.(htmlToMarkdown(html));
    },
    onFocus() {
      onFocus?.();
    },
    onBlur({ editor }) {
      onBlur?.(editor);
    },
    onSelectionUpdate({ editor }) {
      const linkMark = editor.isActive("link")
        ? editor.getAttributes("link").href
        : "";
      editor.storage.customState.existingLink = linkMark;
    },
    editorProps: {
      attributes: {
        autocomplete: "off",
        autocorrect: "off",
      },
      handleDrop(_view, event) {
        // Attachment drags are handled by the surrounding drop zone (the event still bubbles up) — only block the editor from inserting them as inline images.
        return hasAttachmentsDragData(event.dataTransfer);
      },
      handleClick(view, pos, event) {
        const el = event.target as HTMLElement;
        if (el?.tagName?.toLowerCase() === "a") {
          event.preventDefault();
          return true;
        }
        return false;
      },
    },
  });

  // Set editor instance in context
  useEffect(() => {
    if (editorInstance) {
      setEditor(editorInstance);
    }
  }, [editorInstance, setEditor]);

  // Handle disabled state
  useEffect(() => {
    if (editorInstance) {
      editorInstance.setEditable(!disabled);
    }
  }, [editorInstance, disabled]);

  // Expose imperative handle for parent components
  useImperativeHandle(
    ref,
    () => ({
      setContent: (content: string) => {
        if (editorInstance) {
          const html = markdownToHtml(content);
          editorInstance.commands.setContent(html);
        }
      },
      getContent: () => {
        if (editorInstance) {
          const html = stripTags(editorInstance.getHTML());
          return htmlToMarkdown(html);
        }
        return "";
      },
      focus: () => {
        editorInstance?.commands.focus();
      },
      blur: () => {
        editorInstance?.commands.blur();
      },
      isEmpty: () => {
        return editorInstance?.isEmpty ?? true;
      },
      resetContent: () => {
        editorInstance?.commands.resetContent();
      },
    }),
    [editorInstance],
  );

  return (
    <EditorErrorBoundary>
      <EditorContentTiptap
        editor={editorInstance}
        className={cn(
          /* sorted */
          "rich-text-editor",
          `overflow-y-auto min-h-12 max-h-50 px-4 py-3 wrap-break-word text-text-dark-max`,
          "focus-visible:outline-none",
          disabled
            ? "cursor-not-allowed bg-bg-dark-low opacity-50 text-text-dark-min"
            : "",
          className,
        )}
        style={{ wordBreak: "break-word" }}
        data-test="editor-content"
      />
    </EditorErrorBoundary>
  );
});
